<template>
  <div>
    <template v-if="loading || !groupsLoaded">{{ $t('voc.loading') }}</template>
    <template v-if="!loading && groupsLoaded">
      <div style="margin-bottom: 54px">
        <div class="d-flex flex-column">
          <div class="d-flex" v-if="role !== 'employee'">
            <div class="adp-emp-card-for-manager">
              <div class="adp-emp-info-for-manager">
                <span class="adp-p">{{ employee.pernumber }}</span>
                <span class="font-weight-bold">{{ employee.fullname }}</span>
                <span class="adp-p">{{ employee.position }}</span>
                <span class="adp-p">{{ employee.subdivision }}</span>
                <div class="adp-emp-info-col" >
                  <span class="font-weight-bold">{{ managers.length > 1 ? $t('voc.manager') : $t('voc.managers') }}:</span>
                  <span class="adp-p" v-if="managers.length">{{ managers.join(', ') }}</span>
                  <span v-else>{{ $t(`voc.managers_empty`) }}</span>
                </div>
                <div class="adp-emp-info-col" >
                  <span class="adp-p font-weight-bold">
                    {{functionalManagers.length > 1 ? $t('voc.functional_manager') : $t('voc.functional_managers') }}:
                  </span>
                  <span class="adp-p" v-if="functionalManagers.length">{{ functionalManagers.join(', ') }}</span>
                  <span v-else>{{ $t(`voc.functional_managers_empty`) }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex align-self-center ml-5">
              <button class="btn adp-btn adp-btn__report" @click="openReportModal">
                {{ $t('idp.download') }}
              </button>
            </div>
          </div>
          <div class="d-flex align-items-center" :class="{'mt-3': role === 'employee', 'mt-5': role !== 'employee'}">
            <div class="d-flex align-items-center">
              <span class="adp-h1" style="white-space: nowrap">{{ $t('idp.individual_development_plan') }}&nbsp;</span>
              <span class="adp-h2">{{ $t('idp.for') }}&nbsp;</span>

              <my-year-filter :options="IDPYears" :init-selected="filterYears" :has-select-all="true"
                              v-on:my-change="changeYearListener">
              </my-year-filter>
            </div>

            <template v-if="Object.keys(allIDPs).length === 1">
              <div class="adp-p adp-p__grey adp-status-tag mr-2" style="margin: 0 24px 0 35px; min-width: 144px"
                   :class="{'adp-status-tag__green': IDP.status === 'agreed_by_hr',
                 'adp-status-tag__red': IDP.status === 'on_approval' || IDP.status === 'not_formed'}">
                {{ $t(`idp.${IDP.status}`) }}
              </div>

              <img src="/images/info.svg" style="margin: 0 32px 0 24px" v-tooltip.top-start="{ content: $t('idp.status_tooltip'), html: true, autoHide: false }">

              <div>
                <button id="download-button" class="btn adp-btn adp-btn__report mr-2" @click="openReportModal" v-if="role === 'employee'">
                  {{ $t('idp.download') }}
                </button>
                <button v-if="canSendToApproveIDP(role, IDP.status)" class="btn adp-btn mr-2" @click="sendToApproveIDP(defaultYear)">
                  {{ $t('idp.send_to_approve_idp') }}
                </button>
                <button v-if="canApproveIDPAsManager(role, IDP.status)" class="btn adp-btn mr-2" @click="sendToAgreeIDP(defaultYear)">
                  {{ $t('idp.assign_idp') }}
                </button>
                <button v-if="canApproveIDPAsHR(role, IDP.status)" class="btn adp-btn mr-2" @click="sendToAgreeIDP(defaultYear)">
                  {{ $t('idp.assign_idp') }}
                </button>
              </div>
            </template>

            <template>
              <div class="btn adp-btn-outline float-right ml-auto mr-3" v-if="role !== 'admin'" @click="addLearningCM = true">
                + {{ $t('idp.add') }}
              </div>
              <div class="adp-task--crud-actions" style="right: 85px" :style="role === 'employee' ? 'top: 125px' : 'top: 260px'"
                   v-if="addLearningCM" v-click-outside="() => {addLearningCM = false}" >
                <a class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
                   :href="`/catalog?from=idp&pernumber=${employee.pernumber}`">
                  {{ $t('idp.add_from_catalog') }}
                </a>
                <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showIndividualCreateModal('mandatory')">
                  {{ $t('idp.create') }}
                </div>
<!--                <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"-->
<!--                     v-if="role === 'employee' || role === 'manager' || role === 'fmanager'"-->
<!--                     @click="$bvModal.show('wish-create-modal')">-->
<!--                  {{ $t('idp.create_wish') }}-->
<!--                </div>-->
              </div>
            </template>
          </div>
        </div>
      </div>

      <template v-for="year in reverseYears">
        <template v-if="Object.keys(allIDPs).length > 1">
          <div v-on:click="allIDPs[year].visible = !allIDPs[year].visible;" class="adp-h2 adp-collapse-control mb-4" >
            {{ $t('idp.learning_for') }} {{ year }}

            <img :src="`/images/${allIDPs[year].visible ? 'minus' : 'plus'}.svg`" alt="">
            <div class="d-inline-flex">
              <div class="adp-p adp-p__grey adp-status-tag mr-2" style="margin: 0 24px 0 35px"
                   :class="{'adp-status-tag__green': getIdpStatus(year).status === 'agreed_by_hr',
                 'adp-status-tag__red': getIdpStatus(year).status === 'on_approval' || getIdpStatus(year).status === 'not_formed'}">
                {{ $t(`idp.${getIdpStatus(year).status}`) }}
              </div>

              <img src="/images/info.svg" style="margin: 0 32px 0 24px" v-tooltip.top-start="{ content: $t('idp.status_tooltip'), html: true, autoHide: false }">

              <button v-if="canSendToApproveIDP(role, getIdpStatus(year).status)" class="btn adp-btn mr-2" @click="sendToApproveIDP(year)">
                {{ $t('idp.send_to_approve_idp') }}
              </button>
              <button v-if="canApproveIDPAsManager(role, getIdpStatus(year).status)" class="btn adp-btn mr-2"
                      @click="sendToAgreeIDP(year)">
                {{ $t('idp.assign_idp') }}
              </button>
              <button v-if="canApproveIDPAsHR(role, getIdpStatus(year).status)" class="btn adp-btn mr-2"
                      @click="sendToAgreeIDP(year)">
                {{ $t('idp.assign_idp') }}
              </button>
            </div>
          </div>
        </template>

        <div class="collapse" v-collapsible="allIDPs[year].visible">
          <div class="adp-task-group">
            <span class="adp-h2">
              {{ role === 'employee' ? $t('idp.learnings_employee') : $t('idp.learnings') }}
              <img src="/images/info.svg" style="margin: 0 40px 0 11px" v-tooltip.top-start="{ content: $t('idp.learning_tooltip') }">
            </span>
            <div style="position: relative; display: inline">
              <button class="btn btn-primary adp-btn adp-btn__large" @click="showActionsMenu('learningAssignItems', learningAssignItems)">
                {{ $t('voc.actions') }} <img src="/images/arrow.svg" style="padding-left: 10px">
              </button>
              <div class="actions-button-error" v-if="showLearningAssignItemsError"
                   v-click-outside="() => {showLearningAssignItemsError = false}">
                {{ $t('voc.warning_actions_activity') }}
              </div>
            </div>
          </div>
          <template>
            <employee-table-idp-learnings
              :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
              v-on:show-comments-modal="showCommentsViewModal"
              v-on:changeLearningAssignItems="changeLearningAssignItems"
              v-on:learning-click="clickLearningItem"
              :vals="allIDPs[year].learningActivities.filter(la => la.type !== 'wishes') || []"
              :idpYear="year"
            />
          </template>

          <div class="adp-task-group">
            <span class="adp-h2">{{ role === 'employee' ? $t('idp.wishes_txt_employee') : $t('idp.wishes_txt') }}
              <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.wishes_tooltip') }">
            </span>
            <div style="position: relative; display: inline">
              <button class="btn btn-primary adp-btn adp-btn__large" @click="showActionsMenu('wishAssignItems', wishAssignItems)">
                {{ $t('voc.actions') }} <img src="/images/arrow.svg" style="padding-left: 10px">
              </button>
              <div class="actions-button-error" v-if="showWishAssignItemsError"
                   v-click-outside="() => {showWishAssignItemsError = false}">
                {{ $t('voc.warning_wishes_activity') }}
              </div>
            </div>
          </div>
          <template>
            <employee-table-idp-wishes
              :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
              v-on:show-comments-modal="showCommentsViewModal"
              v-on:changeWishAssignItems="changeWishAssignItems"
              v-on:learning-click="clickLearningItem"
              :vals="allIDPs[year].learningActivities
                .filter(la => role === 'employee' ? (la.type === 'wishes' && la.status !== 'approved') : la.type === 'wishes')
                || []"
              :idpYear="year"
            />
          </template>
        </div>
      </template>

      <!-- Context menu -->
      <div class="adp-task--crud-actions" :style="cmStyles" v-if="contextMenu.isVisible"
           v-click-outside="() => hideCM()" ref="ctxm">
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showViewModal()">
          {{ viewOrEditEvent(contextMenu.learningActivity) }}
        </div>
        <div v-if="canAssignActivity(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="assignLearningActivity(contextMenu.learningActivityId)">
          {{ $t('idp.assign') }}
        </div>
        <div v-if="canMarkAsInProgress(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="markAsInProgress(contextMenu.learningActivityId)">
          {{ $t('idp.cm_mark_as_in_process') }}
        </div>
        <div v-if="canMarkAsDone(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="markAsDone(contextMenu.learningActivityId)">
          {{ $t('idp.cm_mark_as_done') }}
        </div>
        <div v-if="canMarkAsNotDone(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="markAsNotDone(contextMenu.learningActivityId)">
          {{ $t('idp.cm_mark_as_not_done') }}
        </div>
        <div v-if="canRejectActivity(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="rejectLearningActivity(contextMenu.learningActivityId)"
             >
          {{ $t('idp.cm_reject') }}
        </div>
        <div v-if="canCancelActivity(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="cancelLearningActivity(contextMenu.learningActivityId)"
        >
          {{ contextMenu.learningActivityType === 'wishes' ? $t('idp.cm_cancel_wish') : $t('idp.cm_cancel') }}
        </div>
        <div v-if="canChangeDeadline(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showChangeDeadlineModal()">
          {{ $t('idp.cm_set_new_deadline') }}
        </div>
        <div v-if="canRemoveActivity(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="deleteLearningActivity(contextMenu.learningActivityId)">
          {{ $t('idp.cm_delete') }}
        </div>
        <div v-if="canRemoveWish(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="deleteWish(contextMenu.activityId)">
          {{ $t('idp.cm_delete') }}
        </div>
        <div v-if="canApprove(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="approveLearningActivity(contextMenu.learningActivityId)">
          {{ $t('idp.cm_approve') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showCommentsCreateModal()">
          {{ $t('idp.cm_comment') }}
        </div>
        <div v-if="canAttachFiles(contextMenu.learningActivityType)" class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="showAttachFilesModal()">
          {{ $t('idp.cm_attach_files') }}
        </div>
        <div v-if="canDuplicateCm(contextMenu.learningActivityType, role)" class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="duplicateEventCm()">
          {{ $t('idp.cm_duplicate') }}
        </div>
      </div>
      <!-- End of context menu -->

      <!-- Actions menu -->
      <div class="adp-task--crud-actions" v-if="canShowActionsMenu" :style="actionsMenuStyles"
           v-click-outside="() => hideActionsMenu()" ref="actions">
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassAssign(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('assigned')">
          {{ $t('idp.cm_assign_hr') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassReject(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('rejected')">
          {{ $t('idp.cm_reject') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassCancel(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('canceled')">
          {{ actionsMenu.currentType === 'wishAssignItems' ? $t('idp.cm_cancel_wish') : $t('idp.cm_cancel') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassMarkInProgress(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('inprogress')">
          {{ $t('idp.cm_mark_as_in_process') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassDone(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('done')">
          {{ $t('idp.cm_mark_as_done') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassNotDone(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('notdone')">
          {{ $t('idp.cm_mark_as_not_done') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassDeadlines(this[actionsMenu.currentType], IDP.status, role)"
             @click="setMassActivitiesDeadlines()">
          {{ $t('idp.cm_set_new_deadline') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassRemoveWishes(this[actionsMenu.currentType], IDP.status, role)"
             @click="massRemoveWishes()">
          {{ $t('idp.cm_delete') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassRemoveActivities(this[actionsMenu.currentType], IDP.status, role)"
             @click="massRemoveActivities()">
          {{ $t('idp.cm_delete') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassApprove(this[actionsMenu.currentType], IDP.status, role)"
             @click="massAssignWishes('approved')">
          {{ $t('idp.cm_approve') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="addMassActivitiesComments()">
          {{ $t('idp.cm_comment') }}
        </div>
        <div v-if="canMassAttachFiles(this[actionsMenu.currentType])"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="attachMassActivitiesFiles()">
          {{ $t('idp.cm_attach_files') }}
        </div>
        <div v-if="canDuplicate(this[actionsMenu.currentType], role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="duplicateEvent()">
          {{ $t('idp.cm_duplicate') }}
        </div>
      </div>
      <!-- End of Actions menu -->
    </template>

    <!--  Modals -->
    <wish-create-modal v-on:wish-created="reloadIDP()"
                       :participant_pernumber="employee ? employee.pernumber : 0"
                       :groups-tree="groupsTree"
                       :group-options="groupOptions"
                       :provider-options="providerOptions"
                       :searched-employees="searchedEmployees"
                       @coachFioInput="debounceSearchCoach"
                       @closeCoachMenu="() => searchedEmployees = []"
    />
    <wish-view-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                     :activity-id="Number(contextMenu.activityId)"
                     :activity-type="String(contextMenu.learningActivityType)"
                     :is-editable="true"
                     v-on:wish-edited="reloadIDP()"
                     :groups-tree="groupsTree"
                     :group-options="groupOptions"
                     :provider-options="providerOptions"
                     :searched-employees="searchedEmployees"
                     @coachFioInput="debounceSearchCoach"
                     @closeCoachMenu="() => searchedEmployees = []"
    />

    <individual-create-modal :learning-activity-group="String(contextMenu.learningActivityGroup)"
                             :employee-type="employee ? String(employee.type) : ''"
                             v-on:individual-created="reloadIDP()"
                             :groups-tree="groupsTree"
                             :group-options="groupOptions"
                             :provider-options="providerOptions"
                             :copy-data="copyData"
                             :searched-employees="searchedEmployees"
                             @coachFioInput="debounceSearchCoach"
                             @closeCoachMenu="() => searchedEmployees = []"
    />
    <individual-view-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                           :activity-id="Number(contextMenu.activityId)"
                           :activity-type="String(contextMenu.learningActivityType)"
                           :pernumber="employee ? Number(employee.pernumber) : 0"
                           :year="Number(contextMenu.year)"
                           v-on:individual-updated="reloadIDP()" :is-editable="role !== 'admin'"
                           :groups-tree="groupsTree"
                           :group-options="groupOptions"
                           :provider-options="providerOptions"
                           :searched-employees="searchedEmployees"
                           @coachFioInput="debounceSearchCoach"
                           @closeCoachMenu="() => searchedEmployees = []"
    />


    <common-event-view-modal :activity-id="Number(contextMenu.activityId)"
                             :activity-type="String(contextMenu.learningActivityType)"
                             :pernumber="employee ? Number(employee.pernumber) : 0"
                             :year="Number(contextMenu.year)"
                             :group-options="groupOptions"
                             :groups-tree="groupsTree"
                             :provider-options="providerOptions"
                             v-on:event-updated="reloadIDP()" :is-editable="role !== 'admin'"
                             :searched-employees="searchedEmployees"
                             @coachFioInput="debounceSearchCoach"
                             @closeCoachMenu="() => searchedEmployees = []"
    />

    <comments-view-modal :comments="contextMenu.comments"
                         :name="contextMenu.activityName"
                         :viewer-pernumber="Number(this.pernumber)"
                         :learning-activity-id="Number(contextMenu.learningActivityId)">
    </comments-view-modal>
    <comments-create-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                           :name="contextMenu.activityName"
                           :viewer-pernumber="Number(this.pernumber)"
                           :provider-options="providerOptions"
                           v-on:comment-created="reloadIDP()"
    />
    <mass-comments-create-modal :activities="[...Object.values(this.massAssignItems)]"
                                :viewer-pernumber="Number(this.pernumber)"
                                v-on:comments-created="reloadIDP()"/>
    <mass-idp-files-attach-modal :activities="[...Object.values(this.massAssignItems)]"
                                :viewer-pernumber="Number(this.pernumber)"
                                v-on:files-attached="reloadIDP()"/>
    <files-list-modal :learning-activity-title="contextMenu.activityName"
                      :learning-activity-id="Number(contextMenu.learningActivityId)"
                      :viewer-pernumber="Number(this.pernumber)"
                      v-on:file-deleted="reloadIDP()"
                      :files="contextMenu.files">
    </files-list-modal>
    <attach-files-modal :learning-activity-title="contextMenu.activityName"
                        :learning-activity-id="Number(contextMenu.learningActivityId)"
                        :viewer-pernumber="Number(this.pernumber)"
                        v-on:files-uploaded="reloadIDP()">
    </attach-files-modal>
    <change-deadline-modal :activity="contextMenu.learningActivity ? contextMenu.learningActivity : {}"
                           v-on:deadline-updated="reloadIDP()" v-if="!Object.values(learningAssignItems).length">
    </change-deadline-modal>
    <div v-if="Object.values(learningAssignItems).length" v-for="(activity, i) in Object.values(learningAssignItems)">
      <change-deadline-modal :activity="activity" v-on:deadline-updated="reloadIDP()" :activity-index="i+1"/>
    </div>
    <idp-export-modal :pernumbers="[employeePernumber]"/>
    <reports-modal v-show="isReportsOpened" :is-reports-opened="isReportsOpened"/>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import EmployeeSearch from "./EmployeeSearch";
import EmployeeSidebar from "./EmployeeSidebar";
import MyTaskFilter from "./MyTaskFilter";
import MyYearFilter from "./MyYearFilter";
import WishCreateModal from "./modals/WishCreateModal";
import WishViewModal from "./modals/WishViewModal";
import IndividualCreateModal from "./modals/IndividualCreateModal";
import IndividualViewModal from "./modals/IndividualViewModal";
import CommonEventViewModal from "./modals/CommonEventViewModal";
import CommentsViewModal from "./modals/CommentsViewModal";
import CommentsCreateModal from "./modals/CommentsCreateModal";
import FilesListModal from "./modals/FilesListModal";
import api from "../api";
import Vue from "vue";
import AttachFilesModal from "./modals/AttachFilesModal";
import ChangeDeadlineModal from "./modals/ChangeDeadlineModal";
import EmployeeTableIDPLearnings from "./EmployeeTableIDPLearnings";
import EmployeeTableIDPWishes from "./EmployeeTableIDPWishes";
import MassCommentsCreateModal from "./modals/MassCommentsCreateModal";
import MassIdpFilesAttachModal from "./modals/MassIdpFilesAttachModal";
import IdpExportModal from "./modals/exports/IdpExport";
import ReportsModal from "./modals/ReportsModal.vue";

export default {
  name: "IDP",
  props: {},
  data: function () {
    return {
      loading: true,
      groupsLoaded: false,
      dragged: false,
      showWishAssignItemsError: false,
      showLearningAssignItemsError: false,

      employeePernumber: 0,
      employee: null,
      managers: [],
      functionalManagers: [],
      IDP: null,
      learningActivities: [],
      allIDPs: {},

      defaultYear: 0,
      filterYears: [],
      IDPYears: [],

      displayDiv: true,

      filterStatuses: [],
      IDPStatuses: [],

      contextMenu: {
        isVisible: false,

        idpId: null,
        learningActivity: null,
        learningActivityId: null,
        activityId: null,
        learningActivityType: null,
        learningActivityStatus: null,

        learningActivityGroup: null,
        year: null,

        activityName: '',
        comments: [],
        files: [],

        top: 0,
        left: 0,
      },

      addLearningCM: false,

      pernumber: 0,
      role: '',

      draggedActivity: {},

      learningAssignItems: {},
      wishAssignItems: {},
      massAssignItems: {},
      actionsMenu: {
        isVisible: false,
        currentType: '',

        top: 0,
        left: 0,
        right: 0,
      },
      reportDropdown: {
        isVisible: false,
        top: 0,
        left: 0,
        right: 0,
      },
      idpStatuses: [],

      reverseYears: [],

      groupsTree: [],
      groupOptions: [],
      searchedEmployees: [],
      providerOptions: [],
      employeesList: [],

      copyData: null,

      reportsButton: document.getElementById('reports-download'),
      reportsLoadingButton: document.getElementById('reports-download-circle'),
      isReportsOpened: false,
    };
  },
  components: {
    'employee-table-idp-learnings': EmployeeTableIDPLearnings,
    'employee-table-idp-wishes': EmployeeTableIDPWishes,
    'change-deadline-modal': ChangeDeadlineModal,
    'attach-files-modal': AttachFilesModal,
    'employee-search': EmployeeSearch,
    'employee-sidebar': EmployeeSidebar,
    'my-task-filter': MyTaskFilter,
    'my-year-filter': MyYearFilter,
    'wish-create-modal': WishCreateModal,
    'wish-view-modal': WishViewModal,
    'individual-create-modal': IndividualCreateModal,
    'individual-view-modal': IndividualViewModal,
    'common-event-view-modal': CommonEventViewModal,
    'comments-view-modal': CommentsViewModal,
    'comments-create-modal': CommentsCreateModal,
    'files-list-modal': FilesListModal,
    'mass-comments-create-modal': MassCommentsCreateModal,
    'mass-idp-files-attach-modal': MassIdpFilesAttachModal,
    'idp-export-modal': IdpExportModal,
    'reports-modal': ReportsModal,
  },
  computed: {
    cmStyles() {
      return {
        top: `${this.contextMenu.top}px`,
        left: `${this.contextMenu.left}px`,
        right: 'unset',
      };
    },
    reportCmStyles() {
      return {
        top: '175px',
        left: 'unset',
        right: '110px',
      };
    },
    actionsMenuStyles() {
      return {
        // top: '540px',
        top: `${this.actionsMenu.top}px`,
        left: `${this.actionsMenu.left}px`,
        right: 'unset',
      };
    },
    canShowActionsMenu() {
      return this.actionsMenu.isVisible &&
        (Object.values(this.learningAssignItems).length || Object.values(this.wishAssignItems).length)
    }
  },
  watch: {
    '$route.params.pernumber': async function (pernumber) {
      this.employee.pernumber = pernumber;
      await this.getIdp(pernumber);
      if (this.$route.query.la_y) {
        const year = this.$route.query.la_y;
        await this.changeYearListener([Number(year)])
      }
    },
    '$route.query.la_y': async function (year) {
      await this.changeYearListener([Number(year)]);
    }
  },
  methods: {
    debounceSearchCoach: debounce(function (input) {
      this.searchEmployees(input);
    }, 700),
    async searchEmployees(coachFio) {
      if (coachFio.length > 2) {
        try {
          let res = await api.searchEmployeesByFio(coachFio, false);
          this.searchedEmployees = res.data;
        } catch (e) {
          console.log(e);
        }
      } else {
        this.searchedEmployees = [];
      }
    },
    openCM(event, idpId, learningActivity) {
      this.contextMenu.idpId = idpId;
      this.contextMenu.learningActivity = learningActivity;
      this.contextMenu.learningActivityId = learningActivity.id;
      this.contextMenu.activityId = learningActivity.activity_id;
      this.contextMenu.learningActivityStatus = learningActivity.status;
      this.contextMenu.learningActivityType = learningActivity.type;
      this.contextMenu.learningActivityGroup = learningActivity.group;
      this.contextMenu.year = learningActivity.year;
      this.contextMenu.activityName = learningActivity.name;
      this.contextMenu.files = learningActivity.files;

      this.contextMenu.isVisible = true;

      this.$nextTick(() => {
        let yOffset = this.$refs.ctxm.clientHeight;
        if (event.pageY + yOffset < document.documentElement.scrollHeight) {
          yOffset = 0;
        }
        this.contextMenu.top = event.pageY - yOffset;
        this.contextMenu.left = event.pageX - this.$refs.ctxm.clientWidth;
      });
    },
    hideCM() {
      this.contextMenu.isVisible = false;
    },
    openReportModal() {
      this.$bvModal.show('idp-export-modal');
    },
    showViewModal() {
      if (this.contextMenu.learningActivityType === 'wishes') {
        this.$bvModal.show('wish-view-modal');
      } else if (this.contextMenu.learningActivityType === 'individual') {
        this.$bvModal.show('individual-view-modal');
      } else {
        this.$bvModal.show('common-event-view-modal');
      }
    },
    clickLearningItem(learningActivity) {
      this.contextMenu.learningActivityId = learningActivity.id;
      this.contextMenu.activityId = learningActivity.activity_id;
      this.contextMenu.learningActivityType = learningActivity.type;
      this.contextMenu.year = learningActivity.year;
      this.contextMenu.learningActivity = learningActivity;

      this.$nextTick(() => {
        return this.showViewModal();
      });
    },
    async changeYearListener(val) {
      if (val.length === 0) {
        val = [this.defaultYear];
      }
      if (val.length === this.IDPYears.length) {
        this.filterYears = val;
      }
      if (val.length !== this.IDPYears.length) {
        this.filterYears = [val[val.length-1]];
      }

      await this.getIdp(this.employee.pernumber);
    },
    async getIdp(pernumber) {
      this.loading = true;
      try {
        let resp = await api.getIDP(pernumber, this.filterYears);
        let idp = resp.data;
        this.employee = idp.employee;
        this.managers = idp.managers;
        this.functional_managers = idp.functional_managers;
        this.IDP = {
          id: idp.activeIdp.id,
          status: idp.activeIdp.status,
        };

        this.IDPYears = idp.years.sort().map(year => {
          return {id: Number(year), code: String(year)}
        });
        let pair = {};

        for (const [year, learningActivities] of Object.entries(idp.idps)) {
          pair[year] = {visible: true, learningActivities};
        }

        this.reverseYears = Object.keys(pair).reverse();

        if (!this.reverseYears.length) {
          pair = {};
          pair[this.defaultYear] = {visible: true, learningActivities: []};
          this.reverseYears = Object.keys(pair).reverse();
        }

        Vue.set(this, 'allIDPs', pair);
        this.idpStatuses = resp.data.statuses;
      } catch (e) {
        console.error(e);
      }
      this.employeePernumber = Number(pernumber);
      this.loading = false;
    },
    formatDate(input) {
      if (!input) {
        return '';
      }

      let datePart = input.match(/\d+/g);
      const year = datePart[0];
      const month = datePart[1];
      const day = datePart[2];

      return day + '.' + month + '.' + year;
    },
    showIndividualCreateModal(group) {
      this.copyData = null;
      this.$nextTick(() => {
        this.contextMenu.learningActivityGroup = group;
        this.$bvModal.show('individual-create-modal');
      })
    },
    async showCommentsViewModal(comments, activityName, activityId) {
      this.contextMenu.comments = comments;
      this.contextMenu.activityName = activityName;
      this.contextMenu.learningActivityId = activityId;
      await this.$bvModal.show('comments-view-modal');
    },
    showCommentsCreateModal() {
      this.$bvModal.show('comments-create-modal');
    },
    showChangeDeadlineModal() {
      this.$bvModal.show('change-deadline-modal');
    },
    async reloadIDP() {
      this.$bvModal.hide('individual-create-modal');
      this.$bvModal.hide('individual-view-modal');
      this.$bvModal.hide('wish-create-modal');
      this.$bvModal.hide('wish-view-modal');
      this.$bvModal.hide('comments-create-modal');
      this.$bvModal.hide('attach-files-modal');
      this.$bvModal.hide('mass-idp-files-attach-modal');

      this.learningAssignItems = {};
      this.wishAssignItems = {};

      await this.getIdp(this.employee.pernumber);
    },
    sendToApproveIDP(year) {
      (async () => {
        try {
          await api.sendToApprove(year);
        } catch (e) {

        }
        await this.reloadIDP();
      })();
    },
    sendToAgreeIDP(year) {
      (async () => {
        try {
          await api.agreeIDP(this.role, this.employee.pernumber, year);
        } catch (e) {

        }
        await this.reloadIDP();
      })();
    },
    async deleteLearningActivity(learningActivityId) {
      try {
        await api.deleteLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async deleteWish(learningActivityId) {
      try {
        await api.deleteWish({id: learningActivityId});
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async approveLearningActivity(learningActivityId) {
      try {
        await api.approveLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async rejectLearningActivity(learningActivityId) {
      try {
        await api.rejectLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async cancelLearningActivity(learningActivityId) {
      try {
        await api.cancelLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async assignLearningActivity(learningActivityId) {
      try {
        await api.assignLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async markAsInProgress(learningActivityId) {
      try {
        await api.inProgressLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async markAsDone(learningActivityId) {
        try {
          await api.completeLearningActivity(learningActivityId);
        } catch (e) {
          console.log(e);
        }

        await this.reloadIDP();
        this.hideCM();
    },
    async markAsNotDone(learningActivityId) {
      try {
        await api.notCompleteLearningActivity(learningActivityId);
      } catch (e) {
        console.log(e);
      }

      await this.reloadIDP();
      this.hideCM();
    },
    async showFilesListModal(files, activityName, activityId) {
      this.contextMenu.files = files;
      this.contextMenu.activityName = activityName;
      this.contextMenu.learningActivityId = activityId;
      await this.$bvModal.show('files-list-modal');
    },
    isAllFilesViewed(files) {
      const notAllFilesViewed = files.find(file => file.is_viewed === false)
      return this.role === 'employee' && !notAllFilesViewed;
    },
    isAllCommentsRead(comments) {
      const notAllCommentsRead = comments.find(comment => comment.is_read === false);
      return this.role === 'employee' && !notAllCommentsRead;
    },
    showAttachFilesModal() {
      this.$bvModal.show('attach-files-modal');
    },
    showActionsMenu(type, items) {
      if (type && !Object.values(this[type]).length) {
        if (type === 'learningAssignItems') this.showLearningAssignItemsError = true;
        if (type === 'wishAssignItems') this.showWishAssignItemsError = true;

        setTimeout(() => {
          this.showWishAssignItemsError = false;
          this.showLearningAssignItemsError = false;
        }, 2000)
        return;
      }
      this.actionsMenu.isVisible = true;
      this.actionsMenu.currentType = type;
      this.massAssignItems = items;

      this.$nextTick(() => {
        this.actionsMenu.left = Number(event.pageX) - 50;
        this.actionsMenu.top = Number(event.pageY) + 30;
      });
    },
    hideActionsMenu() {
      this.actionsMenu.isVisible = false;
    },
    changeLearningAssignItems(items) {
      this.learningAssignItems = items;
      this.hideActionsMenu();

    },
    changeWishAssignItems(items) {
      this.wishAssignItems = items;
      this.hideActionsMenu();
    },
    async massChangeStatusActivities(status) {
      const data = { ids: Object.keys(this.massAssignItems), status: status };
      await api.massActivityChangeStatus(data);
      await this.reloadIDP();
    },
    async massAssignWishes(status) {
      const data = { ids: Object.keys(this.wishAssignItems), status: status };
      await api.massActivityChangeStatus(data);
      await this.reloadIDP();
    },
    async setMassActivitiesDeadlines() {
      this.$bvModal.show('change-deadline-modal');
    },
    async addMassActivitiesComments() {
      this.$bvModal.show('mass-comments-create-modal');
    },
    async attachMassActivitiesFiles() {
      this.$bvModal.show('mass-idp-files-attach-modal');
    },
    async massRemoveActivities() {
      const ids = Object.values(this.learningAssignItems).map(activity => activity.id);
      await api.massDeleteActivities({ids});
      await this.reloadIDP();
      this.hideCM();
    },
    async massRemoveWishes() {
      const ids = Object.values(this.wishAssignItems).map(wish => wish.activity_id);
      await api.massDeleteWishes({ids});
      await this.reloadIDP();
      this.hideCM();
    },
    getIdpStatus(year) {
      return this.idpStatuses.find(x => x.year === Number(year));
    },
    viewOrEditEvent(event) {
      if (this.role === 'hr' && event.type === 'wishes' && event.la_format === 'training') return this.$t('idp.cm_view_edit');
      if (event.type === 'individual' && event.author_role === this.role) return this.$t('idp.cm_view_edit');
      if (event.type === 'wishes' && event.author_role === this.role) return this.$t('idp.cm_view_edit');

      return this.$t('idp.cm_view');
    },
    async getCopyData(activity) {
      let res = await api.getLearningActivity(activity.type, activity.activity_id);
      this.copyData = {};

      this.copyData.activityId = res.data.id;
      this.copyData.ttype = res.data.type;
      this.copyData.name = res.data.name;
      this.copyData.group = res.data.group ? res.data.group.id : '';
      this.copyData.functionModel = res.data.function ? res.data.function.id : '';
      this.copyData.competencies = res.data.competencies ? res.data.competencies : '';
      this.copyData.startdAt = res.data.start_at ? new Date(res.data.start_at) : null;
      this.copyData.endAt = res.data.end_at ? new Date(res.data.end_at) : null;
      this.copyData.description = res.data.description;
      this.copyData.authorRole = res.data.author_role;
      this.copyData.coachFio = res.data.coach_fio;
      this.copyData.format = res.data.la_format;
      this.copyData.type = res.data.la_type;
      this.copyData.resource = res.data.la_resource;
      this.copyData.method = res.data.la_method;
      this.copyData.provider = res.data.provider;
      this.copyData.duration = res.data.duration;
      this.copyData.targetGroup = res.data.target_group;

      this.copyData.link = res.data.link;

      this.copyData.period = [
        res.data.start_at ? new Date(res.data.start_at) : null,
        res.data.end_at ? new Date(res.data.end_at) : null
      ];
      this.copyData.periodForView = [
        res.data.start_at ? res.data.start_at : null,
        res.data.end_at ? res.data.end_at : null
      ];

      this.copyData.currentRole = window.IDP.role;

      this.copyData.programFiles = res.data.programs;
      this.copyData.educationalMaterialFiles = res.data.educationals;
    },
    async duplicateEvent() {
      const activity = Object.values(this.learningAssignItems)[0];
      await this.getCopyData(activity)
        .then(() => this.copyData.deadline = new Date(activity.deadline));
      await this.$bvModal.show('individual-create-modal');
    },
    async duplicateEventCm() {
      const activity = this.contextMenu.learningActivity;
      await this.getCopyData(activity)
        .then(() => this.copyData.deadline = new Date(activity.deadline));
      await this.$bvModal.show('individual-create-modal');
    },
    async getGroupsTree() {
      this.groupsLoaded = false;
      try {
        await api.getCompetenciesList()
          .then((res) => {
            this.groupsTree = res.data;
            this.groupOptions = [...new Set(res.data.filter(x => x.group_is_hidden === 0).map(x => x.group_id))].map(x => {
              return { code: x, label: res.data.find(y => y.group_id === x).group_name };
            });
          })
          .then(() => this.groupsLoaded = true);
      } catch (e) {
        console.log(e);
      }
    },
    async getProviders() {
      try {
        let res = await api.getProviders();
        this.providerOptions = res.data.data.map(el => {
          return {
            code: el.id,
            label: el[`name_${this.$i18n.locale}`],
          };
        });
      } catch (e) {
        console.error(e);
      }
    },
  },
  async mounted() {
    this.reportsButton.onclick = () => this.isReportsOpened = !this.isReportsOpened;
    this.reportsLoadingButton.onclick = () => this.isReportsOpened = !this.isReportsOpened;

    this.defaultYear = new Date().getFullYear();
    this.role = window.IDP.role ? String(window.IDP.role) : 'employee';

    let laStatusArr = window.IDP.learningActivityStatuses;
    for (let status of laStatusArr) {
      this.IDPStatuses.push({id: status, code: status,},)
    }

    const currState = Object.fromEntries(new URLSearchParams(window.location.search));
    let filterYears = [];
    if (currState.la_y) {
      filterYears = [Number(currState.la_y)];
    }
    this.filterYears = [new Date().getFullYear()];

    this.pernumber = window.IDP.pernumber ? Number(window.IDP.pernumber) : 0;

    let employeePernumber = '0';
    if (this.$route && this.$route.params.pernumber) {
      employeePernumber = this.$route.params.pernumber;
    } else if (window.IDP.pernumber && window.IDP.role === 'admin') {
      employeePernumber = window.IDP.pernumber;
    } else if (window.IDP.pernumber && (window.IDP.role === '' || window.IDP.role === 'employee')) {
      employeePernumber = window.IDP.pernumber;
    }

    await this.getIdp(employeePernumber);
    await this.getGroupsTree();
    await this.getProviders();

    // modal hooks
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'files-list-modal') {
        api.getActivityFiles(this.contextMenu.learningActivityId)
        .then(response => this.contextMenu.files = response.data)

        this.contextMenu.files.forEach(async (file) => {
          await api.setFileViewed(file.id);
        })
      }
      if (modalId === 'comments-view-modal') {
        api.getActivityComments(this.contextMenu.learningActivityId)
          .then(response => this.contextMenu.comments = response.data)

        this.contextMenu.comments.forEach(async (comment) => {
          if (comment.id !== 0) await api.readComment(comment.id);
        })
      }
    })

    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId === 'mass-wish-approval-modal') this.reloadIDP();
    })

    this.$root.$on('mass-change-status-modal-save', (pernumbers) => {
      if (pernumbers.includes(this.employeePernumber)) this.reloadIDP();
    })
  }
}
</script>

<style scoped>
  @media screen and (max-width: 1650px) {
    #download-button {
      margin-bottom: 8px;
    }
  }
</style>
